import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { routePaths } from '@/lib/router'
import { useAuthStore } from '@/stores/auth'
import type { SwitchableWorkspace } from './useWorkspaceSwitch.types'

// The one organization-switch sequence shared by the sidebar and workspace
// management page. The store owns the atomic tenant-context replacement.
export function useWorkspaceSwitch() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const switchOrganization = useAuthStore((state) => state.switchOrganization)
  const [isSwitching, setIsSwitching] = useState(false)

  // Resolves true when the workspace is the active tenant afterwards.
  const switchWorkspace = async (
    workspace: SwitchableWorkspace
  ): Promise<boolean> => {
    if (workspace.isCurrent) {
      toast.info('Already in this workspace')

      return true
    }

    try {
      setIsSwitching(true)
      toast.loading('Switching workspace...', { id: 'workspace-switch' })

      await switchOrganization(workspace.id)

      toast.success('Workspace switched successfully!', {
        id: 'workspace-switch',
      })

      await queryClient.invalidateQueries()
      navigate(routePaths.dashboard)

      return true
    } catch (error) {
      console.error('Error switching workspace:', error)

      const message = error instanceof Error ? error.message : 'Unknown error'

      toast.error(`Failed to switch workspace: ${message}`, {
        id: 'workspace-switch',
      })

      return false
    } finally {
      setIsSwitching(false)
    }
  }

  return { switchWorkspace, isSwitching }
}
